import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { TrendingUp, Users, Package, FileText, DollarSign, AlertTriangle, Clock, BarChart3, Mail, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

const STATUS_LABELS = {
  pending: 'Pendiente',
  received: 'Recibido',
  in_warehouse: 'En Almacen',
  in_repack: 'En Reempaque',
  shipped: 'Enviado',
  delivered: 'Entregado',
  invoiced: 'Facturado',
  paid: 'Pagado',
  pending_reception: 'En Transito'
};

const AdminAnalytics = ({ token }) => {
  const [overview, setOverview] = useState(null);
  const [monthlyActivity, setMonthlyActivity] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [statusDist, setStatusDist] = useState({ trackings: [], warehouse_receipts: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trackings');
  const [sendingSummary, setSendingSummary] = useState(false);
  const [summaryResult, setSummaryResult] = useState(null);

  const axiosConfig = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, monthlyRes, topRes, statusRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`, axiosConfig),
        axios.get(`${API}/admin/analytics/monthly-activity`, axiosConfig),
        axios.get(`${API}/admin/analytics/top-clients`, axiosConfig),
        axios.get(`${API}/admin/analytics/status-distribution`, axiosConfig),
      ]);
      setOverview(overviewRes.data);
      setMonthlyActivity(monthlyRes.data);
      setTopClients(topRes.data);
      setStatusDist(statusRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [axiosConfig]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleSendWeeklySummary = async () => {
    if (!window.confirm('¿Enviar resumen semanal por email a todos los clientes con paquetes pendientes o facturas abiertas?')) return;
    setSendingSummary(true);
    setSummaryResult(null);
    try {
      const res = await axios.post(`${API}/admin/send-weekly-summary`, {}, axiosConfig);
      setSummaryResult(res.data);
    } catch (err) {
      setSummaryResult({ error: err.response?.data?.detail || err.message });
    } finally {
      setSendingSummary(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border shadow-lg rounded-lg p-3 text-sm">
          <p className="font-semibold text-gray-800 mb-1">{label}</p>
          {payload.map((entry, i) => (
            <p key={i} style={{ color: entry.color }} className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: entry.color }}></span>
              {entry.name}: <strong>{entry.name === 'Ingresos' ? `$${entry.value.toFixed(2)}` : entry.value}</strong>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 bg-gray-200 rounded w-16"></div>
                  <div className="h-3 bg-gray-200 rounded w-32"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card><CardContent className="pt-6"><div className="animate-pulse h-64 bg-gray-100 rounded"></div></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="text-blue-600" size={28} />
            Panel de Reportes y Analiticas
          </h1>
          <p className="text-sm text-gray-500 mt-1">Vision general del negocio en tiempo real</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={handleSendWeeklySummary}
            disabled={sendingSummary}
            variant="outline"
            className="gap-2 text-sm"
            data-testid="send-weekly-summary-btn"
          >
            {sendingSummary ? (
              <><Clock size={16} className="animate-spin" /> Enviando...</>
            ) : (
              <><Mail size={16} /> Enviar Resumen Semanal</>
            )}
          </Button>
          <button 
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
            data-testid="refresh-analytics-btn"
          >
            Actualizar Datos
          </button>
        </div>
      </div>

      {/* Weekly Summary Result */}
      {summaryResult && (
        <div className={`rounded-lg p-4 text-sm ${summaryResult.error ? 'bg-red-50 border border-red-200 text-red-800' : summaryResult.errors > 0 ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-green-50 border border-green-200 text-green-800'}`} data-testid="summary-result">
          {summaryResult.error ? (
            <p>Error: {summaryResult.error}</p>
          ) : (
            <div className="flex items-center gap-4">
              <Send size={18} />
              <span><strong>{summaryResult.sent}</strong> emails enviados</span>
              <span className="text-gray-500">|</span>
              <span>{summaryResult.skipped} sin pendientes</span>
              {summaryResult.errors > 0 && <span className="text-red-600">{summaryResult.errors} errores</span>}
            </div>
          )}
        </div>
      )}

      {/* KPI Cards - Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Clientes Totales</p>
                <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="kpi-total-clients">{overview?.total_clients || 0}</p>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg"><Users className="text-blue-600" size={22} /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Paquetes Totales</p>
                <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="kpi-total-trackings">{overview?.total_trackings || 0}</p>
                <p className="text-xs text-emerald-600 mt-1">+{overview?.month_trackings || 0} este mes</p>
              </div>
              <div className="p-2 bg-emerald-50 rounded-lg"><Package className="text-emerald-600" size={22} /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Warehouse Receipts</p>
                <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="kpi-total-wrs">{overview?.total_wrs || 0}</p>
                <p className="text-xs text-purple-600 mt-1">+{overview?.month_wrs || 0} este mes</p>
              </div>
              <div className="p-2 bg-purple-50 rounded-lg"><FileText className="text-purple-600" size={22} /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Ingresos Totales</p>
                <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="kpi-total-revenue">${overview?.total_revenue?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-amber-600 mt-1">${overview?.pending_revenue?.toFixed(2) || '0.00'} pendiente</p>
              </div>
              <div className="p-2 bg-amber-50 rounded-lg"><DollarSign className="text-amber-600" size={22} /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards - Row 2 (Actionable) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={`${overview?.pending_alerts > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50'}`}>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${overview?.pending_alerts > 0 ? 'bg-red-100' : 'bg-gray-200'}`}>
                <AlertTriangle className={overview?.pending_alerts > 0 ? 'text-red-600' : 'text-gray-400'} size={22} />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Alertas Pendientes</p>
                <p className="text-2xl font-bold" data-testid="kpi-pending-alerts">{overview?.pending_alerts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`${overview?.pending_repacks > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50'}`}>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${overview?.pending_repacks > 0 ? 'bg-yellow-100' : 'bg-gray-200'}`}>
                <Clock className={overview?.pending_repacks > 0 ? 'text-yellow-600' : 'text-gray-400'} size={22} />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Reempaques por Aprobar</p>
                <p className="text-2xl font-bold" data-testid="kpi-pending-repacks">{overview?.pending_repacks || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`${overview?.pending_invoice_count > 0 ? 'bg-orange-50 border-orange-200' : 'bg-gray-50'}`}>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${overview?.pending_invoice_count > 0 ? 'bg-orange-100' : 'bg-gray-200'}`}>
                <TrendingUp className={overview?.pending_invoice_count > 0 ? 'text-orange-600' : 'text-gray-400'} size={22} />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Facturas Pendientes</p>
                <p className="text-2xl font-bold" data-testid="kpi-pending-invoices">{overview?.pending_invoice_count || 0}</p>
                <p className="text-xs text-orange-600">${overview?.pending_revenue?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Activity Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp size={20} className="text-blue-600" />
            Actividad Mensual (Ultimos 12 meses)
          </CardTitle>
          <CardDescription>Paquetes recibidos, Warehouse Receipts creados e ingresos por mes</CardDescription>
          <div className="flex gap-2 mt-2">
            {['trackings', 'revenue'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  activeTab === tab ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab === 'trackings' ? 'Paquetes & WRs' : 'Ingresos'}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              {activeTab === 'trackings' ? (
                <BarChart data={monthlyActivity} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="short" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="trackings" name="Paquetes" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="warehouse_receipts" name="WRs" fill="#16a34a" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="invoices" name="Facturas" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              ) : (
                <AreaChart data={monthlyActivity} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="short" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Area type="monotone" dataKey="revenue" name="Ingresos" stroke="#16a34a" fill="#dcfce7" strokeWidth={2} />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Bottom Row: Top Clients + Status Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Clients */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users size={20} className="text-blue-600" />
              Top 10 Clientes Mas Activos
            </CardTitle>
            <CardDescription>Ordenados por cantidad de paquetes</CardDescription>
          </CardHeader>
          <CardContent>
            {topClients.length > 0 ? (
              <div className="space-y-2">
                {topClients.map((client, index) => (
                  <div key={client.client_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors" data-testid={`top-client-${index}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-blue-400'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{client.name}</p>
                      <p className="text-xs text-gray-500 truncate">{client.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-blue-600">{client.trackings} pkg</p>
                      <p className="text-xs text-gray-500">{client.warehouse_receipts} WRs</p>
                    </div>
                    <div className="text-right min-w-[80px]">
                      <p className="text-sm font-bold text-emerald-600">${client.revenue.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No hay datos de clientes aun</p>
            )}
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package size={20} className="text-purple-600" />
              Distribucion por Estado
            </CardTitle>
            <CardDescription>Estado actual de paquetes y Warehouse Receipts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              {/* Trackings Pie */}
              <div>
                <p className="text-sm font-semibold text-center mb-2">Paquetes</p>
                {statusDist.trackings.length > 0 ? (
                  <>
                    <div className="h-44">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusDist.trackings}
                            cx="50%" cy="50%"
                            innerRadius={35} outerRadius={65}
                            paddingAngle={2}
                            dataKey="count"
                            nameKey="status"
                          >
                            {statusDist.trackings.map((_, index) => (
                              <Cell key={index} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name) => [value, STATUS_LABELS[name] || name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-1 mt-2">
                      {statusDist.trackings.map((item, index) => (
                        <div key={item.status} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                            <span className="text-gray-600">{STATUS_LABELS[item.status] || item.status}</span>
                          </div>
                          <span className="font-semibold">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-center text-gray-400 text-sm py-8">Sin datos</p>
                )}
              </div>

              {/* WRs Pie */}
              <div>
                <p className="text-sm font-semibold text-center mb-2">Warehouse Receipts</p>
                {statusDist.warehouse_receipts.length > 0 ? (
                  <>
                    <div className="h-44">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusDist.warehouse_receipts}
                            cx="50%" cy="50%"
                            innerRadius={35} outerRadius={65}
                            paddingAngle={2}
                            dataKey="count"
                            nameKey="status"
                          >
                            {statusDist.warehouse_receipts.map((_, index) => (
                              <Cell key={index} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name) => [value, STATUS_LABELS[name] || name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-1 mt-2">
                      {statusDist.warehouse_receipts.map((item, index) => (
                        <div key={item.status} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                            <span className="text-gray-600">{STATUS_LABELS[item.status] || item.status}</span>
                          </div>
                          <span className="font-semibold">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-center text-gray-400 text-sm py-8">Sin datos</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalytics;
